import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StructureType, StructureTypeCreateDTO } from '../types/fields';

@Injectable({
  providedIn: 'root'
})
export class StructuresService {
  private apiUrl = `${environment.apiUrl}/structure-types`;

  private structuresSubject = new BehaviorSubject<StructureType[] | null>(null);
  private structuresCache$: Observable<StructureType[]> | null = null;

  constructor(private http: HttpClient) { }

  getStructures(): Observable<StructureType[]> {
    if (!this.structuresCache$) {
      this.structuresCache$ = this.http.get<StructureType[]>(this.apiUrl).pipe(
        tap(structures => this.structuresSubject.next(structures)),
        shareReplay(1)
      );
    }
    return this.structuresCache$;
  }

  getStructureById(id: string): Observable<StructureType> {
    return this.http.get<StructureType>(`${this.apiUrl}/${id}`);
  }

  createStructure(structure: StructureTypeCreateDTO): Observable<StructureType> {
    return this.http.post<StructureType>(this.apiUrl, structure).pipe(
      tap(() => this.clearCache())
    );
  }

  updateStructure(id: string, structure: Partial<StructureTypeCreateDTO>): Observable<StructureType> {
    return this.http.put<StructureType>(`${this.apiUrl}/${id}`, structure).pipe(
      tap(() => this.clearCache())
    );
  }

  deleteStructure(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => this.clearCache())
    );
  }

  refreshStructuresCache(): Observable<StructureType[]> {
    this.clearCache();
    return this.getStructures();
  }

  private clearCache(): void {
    this.structuresCache$ = null;
    this.structuresSubject.next(null);
  }
}
